import {t} from '@lingui/macro';
import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {isTeamNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {makeMapStateToProps} from '../../../../../helpers/reselectHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityTypesEnum,
} from '../../../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../../models/api/activitySourceModel';
import {ConversationTeamStatusesEnum} from '../../../../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {AppState} from '../../../../../store/appState';
import {makeGetConversationInboxes} from '../../../../../store/data/conversations/conversationsSelectors';
import {getTimeFormat} from '../../../../../store/data/preferences/preferencesSelectors';
import {getFindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {getUser, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {getLocalize} from '../../../../../store/stateless/internationalizationSelectors';
import {StaticViewer, StaticViewerReminder} from './staticViewer';

/*
 * Props.
 */

interface StaticViewerContainerProps {
  conversation: ConversationModel;
  activities: ReadonlyArray<ActivityModel>;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: StaticViewerContainerProps) {
  return props.conversation;
}

function getActivities(_state: AppState, props: StaticViewerContainerProps) {
  return props.activities;
}

function makeGetTeamInboxes() {
  const getConversationInboxes = makeGetConversationInboxes(getConversation);
  return createSelector(getConversationInboxes, (conversationInboxes) =>
    conversationInboxes.filter((i) => isTeamNamespace(i.namespace)),
  );
}

function makeGetTeamReminder() {
  return createSelector(
    [getLocalize, getUser, getConversation, getFindTeammate],
    (localize, user, conversation, findTeammate): StaticViewerReminder | undefined => {
      // Find the first team reminder with a valid teammate.
      const reminder = conversation.reminders.find((r) => r.isTeamReminder);
      const teammate =
        reminder && findTeammate(reminder.teammateId, 'StaticViewerContainer makeGetTeamReminder');
      if (!reminder || !teammate) {
        return undefined;
      }

      const teammateName =
        user.id === teammate.id
          ? localize(t('activity-static-reminder-you"')`you`)
          : buildTeammateDisplayName(teammate);

      // Package both the reminder and the teammate.
      return {reminder, teammateName};
    },
  );
}

/** Find the activity corresponding to the conversation's team status.  */
function makeGetTeamActivity() {
  return createSelector([getConversation, getActivities], (conversation, activities) => {
    const activityType = computeActivityTypeForTeamStatus(conversation.teamStatus);

    if (!activityType) {
      return undefined;
    }

    // Find the teammate for the most recent activity based on the current status.
    return _(activities)
      .map((a) => a.visualType === ActivityVisualTypesEnum.BASIC && a)
      .compact()
      .findLast((a) => a.type === activityType);
  });
}

function makeGetTeamActivityDate(getTeamActivity: ReturnType<typeof makeGetTeamActivity>) {
  return createSelector([getTeamActivity], (activity) => activity?.date);
}

/** Find the teammate id associated with the activity corresponding to the conversation's team status.  */
function makeGetTeamActivityTeammateId(getTeamActivity: ReturnType<typeof makeGetTeamActivity>) {
  return createSelector(
    [getTeamActivity],
    (lastActivity) =>
      lastActivity?.source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE &&
      lastActivity?.source.teammateId,
  );
}

/** Find the teammate associated with the activity corresponding to the conversation's team status.  */
function makeGetTeamActivityTeammate(
  getTeamActivityTeammateId: ReturnType<typeof makeGetTeamActivityTeammateId>,
) {
  return createSelector(
    [getFindTeammate, getTeamActivityTeammateId],
    (findTeammate, teamActivityTeammateId) => {
      const teammate =
        teamActivityTeammateId &&
        findTeammate(teamActivityTeammateId, 'StaticViewerContainer makeGetTeamActivityTeammate');
      if (!teammate) {
        return undefined;
      }

      return teammate;
    },
  );
}

/** Find the name of the activity teammate, and account for case where it is the current user.  */
function makeGetTeamActivityTeammateName(
  getTeamActivityTeammate: ReturnType<typeof makeGetTeamActivityTeammate>,
) {
  return createSelector(
    [getLocalize, getUser, getTeamActivityTeammate],
    (localize, user, teamActivityTeammate) => {
      if (!teamActivityTeammate) {
        return '';
      }

      return user.id === teamActivityTeammate.id
        ? localize(t('activity-static-body-you"')`You`)
        : buildTeammateDisplayName(teamActivityTeammate);
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<StaticViewerContainerProps>()(() => {
  const getTeamActivity = makeGetTeamActivity();
  const getTeamActivityDate = makeGetTeamActivityDate(getTeamActivity);
  const getTeamActivityTeammateId = makeGetTeamActivityTeammateId(getTeamActivity);
  const getTeamActivityTeammate = makeGetTeamActivityTeammate(getTeamActivityTeammateId);

  return {
    userId: getUserId,
    teamInboxes: makeGetTeamInboxes(),
    teamReminder: makeGetTeamReminder(),
    timeFormat: getTimeFormat,
    teamActivityTeammateName: makeGetTeamActivityTeammateName(getTeamActivityTeammate),
    teamActivityDate: getTeamActivityDate,
  };
});

export const StaticViewerContainer = connect(mapStateToProps)(StaticViewer);

/*
 * Helpers.
 */

function computeActivityTypeForTeamStatus(teamStatus: ConversationTeamStatusesEnum) {
  if (teamStatus === ConversationTeamStatusesEnum.ARCHIVED) {
    return BasicActivityTypesEnum.ARCHIVE;
  }

  if (teamStatus === ConversationTeamStatusesEnum.TRASHED) {
    return BasicActivityTypesEnum.TRASH;
  }

  return undefined;
}
